/**
 * Position-Based Fluids (PBF) Solver Implementation
 *
 * Implements the Position-Based Fluids method using iterative
 * constraint projection for incompressibility enforcement.
 *
 * Key Features:
 * - Position-based constraint solving
 * - Tensile instability correction
 * - XSPH velocity smoothing
 * - Direct position updates (kendiIntegrasyonuVar = true)
 *
 * Pipeline Stages:
 * 1. Predict positions with external forces
 * 2. Constraint solve loop (3 iterations default):
 *    a. Compute lambda (constraint scaling factors)
 *    b. Compute position corrections with tensile stability
 *    c. Apply position corrections
 * 3. Update velocity from position change + XSPH smoothing
 *
 * Reference: Macklin & Müller 2013 "Position Based Fluids"
 *
 * @see ISPHSolver
 */

import type { ISPHSolver, GPUSolverBuffers } from './ISPHSolver';
import type { PBFParametreler } from '../FikoSPHParametreler';
import {
  PBF_PREDICT_POSITIONS,
  PBF_COMPUTE_LAMBDA,
  PBF_COMPUTE_DELTA_POS,
  PBF_APPLY_DELTA_POS,
  PBF_UPDATE_VELOCITY_XSPH,
  PBF_SOLVER_BINDINGS
} from './sph-pbf-shaders.wgsl';

/**
 * FikoPBFSolver - GPU-accelerated PBF implementation
 *
 * Uses 5 compute pipelines for the PBF algorithm:
 * - Position prediction
 * - Lambda computation
 * - Delta position computation
 * - Delta position application
 * - Velocity update with XSPH
 */
export class FikoPBFSolver implements ISPHSolver {
  readonly ad = 'PBF';
  readonly passSayisi = 6; // 1 predict + 3*(lambda+deltaPos+apply) + 1 velocity
  readonly kendiIntegrasyonuVar = true; // PBF updates positions itself

  private device: GPUDevice | null = null;
  private maxParticle: number = 0;

  // Pipeline infrastructure
  private pipelines: Map<string, GPUComputePipeline> = new Map();

  // Solver-specific buffers
  private solverBuffersRef: GPUSolverBuffers | null = null;
  private currentBindGroup: GPUBindGroup | null = null;

  // Solver parameters
  private params: PBFParametreler = {
    pbfIter: 3,
    pbfEpsilon: 600,
    pbfSCorr_k: 0.1,
    pbfSCorr_n: 4,
    xsphC: 0.01
  };

  /**
   * Initialize the solver with GPU device and constraints
   */
  async basla(
    device: GPUDevice,
    maxParticle: number,
    ortakBindGroupLayout: GPUBindGroupLayout
  ): Promise<void> {
    this.device = device;
    this.maxParticle = maxParticle;

    // Create 5 compute pipelines with auto layout
    await this.createPipelines();
  }

  /**
   * Create all 5 PBF compute pipelines
   */
  private async createPipelines(): Promise<void> {
    if (!this.device) {
      throw new Error('Device not initialized');
    }

    const pipelineConfigs = [
      { name: 'predictPositions', shader: PBF_PREDICT_POSITIONS, label: 'PBF Predict Positions' },
      { name: 'computeLambda', shader: PBF_COMPUTE_LAMBDA, label: 'PBF Compute Lambda' },
      { name: 'computeDeltaPos', shader: PBF_COMPUTE_DELTA_POS, label: 'PBF Compute Delta Pos' },
      { name: 'applyDeltaPos', shader: PBF_APPLY_DELTA_POS, label: 'PBF Apply Delta Pos' },
      { name: 'updateVelocityXSPH', shader: PBF_UPDATE_VELOCITY_XSPH, label: 'PBF Update Velocity XSPH' }
    ];

    for (const config of pipelineConfigs) {
      const shaderModule = this.device.createShaderModule({
        label: `${config.label} Shader`,
        code: config.shader
      });

      const pipeline = this.device.createComputePipeline({
        label: config.label,
        layout: 'auto',
        compute: {
          module: shaderModule,
          entryPoint: 'main'
        }
      });

      this.pipelines.set(config.name, pipeline);
    }
  }

  /**
   * Create solver-specific GPU buffers
   *
   * PBF requires:
   * - lambda: Constraint scaling factors (f32 per particle)
   * - deltaPos: Position corrections (vec4 per particle)
   * - predictedPos: Predicted positions (vec4 per particle)
   */
  olusturBufferlar(device: GPUDevice, maxParticle: number): GPUSolverBuffers {
    const lambdaBuffer = device.createBuffer({
      label: 'PBF Lambda Buffer',
      size: maxParticle * 4, // f32 per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const deltaPosBuffer = device.createBuffer({
      label: 'PBF Delta Position Buffer',
      size: maxParticle * 16, // vec4<f32> per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const predictedPosBuffer = device.createBuffer({
      label: 'PBF Predicted Position Buffer',
      size: maxParticle * 16, // vec4<f32> per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const buffers = new Map<string, GPUBuffer>([
      ['lambda', lambdaBuffer],
      ['deltaPos', deltaPosBuffer],
      ['predictedPos', predictedPosBuffer]
    ]);

    return {
      buffers,
      totalBytes: maxParticle * 36, // 4 + 16 + 16 bytes per particle
      dispose: () => {
        lambdaBuffer.destroy();
        deltaPosBuffer.destroy();
        predictedPosBuffer.destroy();
      }
    };
  }

  /**
   * Get the bind group layout for solver buffers
   *
   * Returns layout from first pipeline (all pipelines share same @group(3) layout)
   */
  solverBindGroupLayout(): GPUBindGroupLayout {
    const pipeline = this.pipelines.get('predictPositions');
    if (!pipeline) {
      throw new Error('Pipelines not initialized');
    }
    return pipeline.getBindGroupLayout(3);
  }

  /**
   * Create bind group for solver buffers
   *
   * @group(3) bindings:
   * - @binding(0): lambda buffer (storage, read_write)
   * - @binding(1): deltaPos buffer (storage, read_write)
   * - @binding(2): predictedPos buffer (storage, read_write)
   */
  olusturBindGroup(device: GPUDevice, buffers: GPUSolverBuffers): GPUBindGroup {
    const layout = this.solverBindGroupLayout();

    const lambdaBuffer = buffers.buffers.get('lambda');
    const deltaPosBuffer = buffers.buffers.get('deltaPos');
    const predictedPosBuffer = buffers.buffers.get('predictedPos');

    if (!lambdaBuffer || !deltaPosBuffer || !predictedPosBuffer) {
      throw new Error('Required PBF buffers not found');
    }

    this.currentBindGroup = device.createBindGroup({
      label: 'PBF Solver Bind Group',
      layout,
      entries: [
        {
          binding: 0,
          resource: { buffer: lambdaBuffer }
        },
        {
          binding: 1,
          resource: { buffer: deltaPosBuffer }
        },
        {
          binding: 2,
          resource: { buffer: predictedPosBuffer }
        }
      ]
    });

    this.solverBuffersRef = buffers;
    return this.currentBindGroup;
  }

  /**
   * Encode all PBF solver compute passes
   *
   * Execution order:
   * 1. Predict positions with external forces
   * 2. Constraint solve loop (default 3 iterations):
   *    - Compute lambda (constraint scaling)
   *    - Compute position corrections with tensile stability
   *    - Apply position corrections
   * 3. Update velocity from position change + XSPH smoothing
   */
  encodeSolverPasslari(
    encoder: GPUCommandEncoder,
    ortakBindGroup: GPUBindGroup,
    particleBindGroup: GPUBindGroup,
    gridBindGroup: GPUBindGroup,
    solverBindGroup: GPUBindGroup,
    workgroupCount: number
  ): void {
    // Pass 1: Predict positions
    this.encodePass(
      encoder,
      'predictPositions',
      'PBF Predict Positions',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );

    // Constraint solve loop
    for (let i = 0; i < this.params.pbfIter; i++) {
      // Pass 2a: Compute lambda
      this.encodePass(
        encoder,
        'computeLambda',
        `PBF Compute Lambda Iter #${i + 1}`,
        ortakBindGroup,
        particleBindGroup,
        gridBindGroup,
        solverBindGroup,
        workgroupCount
      );

      // Pass 2b: Compute delta positions
      this.encodePass(
        encoder,
        'computeDeltaPos',
        `PBF Compute Delta Pos Iter #${i + 1}`,
        ortakBindGroup,
        particleBindGroup,
        gridBindGroup,
        solverBindGroup,
        workgroupCount
      );

      // Pass 2c: Apply delta positions
      this.encodePass(
        encoder,
        'applyDeltaPos',
        `PBF Apply Delta Pos Iter #${i + 1}`,
        ortakBindGroup,
        particleBindGroup,
        gridBindGroup,
        solverBindGroup,
        workgroupCount
      );
    }

    // Pass 3: Update velocity with XSPH smoothing
    this.encodePass(
      encoder,
      'updateVelocityXSPH',
      'PBF Update Velocity XSPH',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );
  }

  /**
   * Encode a single compute pass with proper bind group setup
   */
  private encodePass(
    encoder: GPUCommandEncoder,
    pipelineName: string,
    label: string,
    ortakBindGroup: GPUBindGroup,
    particleBindGroup: GPUBindGroup,
    gridBindGroup: GPUBindGroup,
    solverBindGroup: GPUBindGroup,
    workgroupCount: number
  ): void {
    const pipeline = this.pipelines.get(pipelineName);
    if (!pipeline) {
      console.warn(`PBF pipeline '${pipelineName}' not found`);
      return;
    }

    const pass = encoder.beginComputePass({ label });
    pass.setPipeline(pipeline);

    // Bind groups:
    // @group(0): Uniform parameters (ortak)
    // @group(1): Particle data (positions, velocities, etc.)
    // @group(2): Grid data (spatial hashing)
    // @group(3): Solver-specific data (lambda, deltaPos, predictedPos)
    pass.setBindGroup(0, ortakBindGroup);
    pass.setBindGroup(1, particleBindGroup);
    pass.setBindGroup(2, gridBindGroup);
    pass.setBindGroup(3, solverBindGroup);

    pass.dispatchWorkgroups(workgroupCount);
    pass.end();
  }

  /**
   * Update solver parameters at runtime
   */
  guncelleParametreler(params: Partial<PBFParametreler>): void {
    this.params = { ...this.params, ...params };

    // Recalculate total pass count if pbfIter changed
    if (params.pbfIter !== undefined) {
      const newPassCount = 2 + (this.params.pbfIter * 3); // predict + iter*(lambda+delta+apply) + velocity
      (this as any).passSayisi = newPassCount;
    }
  }

  /**
   * Cleanup GPU resources
   */
  temizle(): void {
    // Dispose pipelines
    this.pipelines.clear();

    // Dispose solver buffers if we created them
    if (this.solverBuffersRef) {
      this.solverBuffersRef.dispose();
      this.solverBuffersRef = null;
    }

    this.currentBindGroup = null;
    this.device = null;
  }

  /**
   * Get all shader source code for debugging/inspection
   */
  shaderKaynagi(): string {
    return [
      '// ===== PBF Solver Bindings =====',
      PBF_SOLVER_BINDINGS,
      '',
      '// ===== 1. Predict Positions =====',
      PBF_PREDICT_POSITIONS,
      '',
      '// ===== 2a. Compute Lambda =====',
      PBF_COMPUTE_LAMBDA,
      '',
      '// ===== 2b. Compute Delta Pos =====',
      PBF_COMPUTE_DELTA_POS,
      '',
      '// ===== 2c. Apply Delta Pos =====',
      PBF_APPLY_DELTA_POS,
      '',
      '// ===== 3. Update Velocity XSPH =====',
      PBF_UPDATE_VELOCITY_XSPH
    ].join('\n');
  }
}

// Export both as default and named export
export default FikoPBFSolver;
